import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useDispatch } from "react-redux";

import ProviderProfileView from "./components/ProviderProfileView";
import ProviderProfileForm from "./components/ProviderProfileForm";
import SecuritySection from "./SecuritySection";
import ChangePasswordModal from "./ChangePasswordModal";

import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import AlertComponent from "../../../common/components/alert-component/alert-component";

import { getProviderProfileDetails } from "../../../api/provider-section/provider-profile.api";
import { userLogout } from "../../../api/auth";
import { logoutByUserType, updateSessionDetails } from "../../../common/routines/user-operations";
import { getCurrentUserDetails } from "../../../common/routines/user-operations";
import LinkPartnerSection from "./LinkPartnerSection";

import "./provider-profile.scss";

type ProviderProfileDetails = {
  first_name?: string;
  last_name?: string;
  email?: string;
  phoneNumber?: string;
  providerId?: string | number;
  provider_id?: string | number;
  profile_photo?: string | null;
  address?: {
    id?: string | number;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipcode?: string;
  } | null;
};

const ProviderProfilePage: React.FC = () => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();

  const [providerProfileDetails, setProviderProfileDetails] =
    useState<ProviderProfileDetails | null>(null);

  const [openChangePwd, setOpenChangePwd] = useState(false);

  // ✅ alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<
    "success" | "error" | "info" | "warning"
  >("success");

  const breadcrumbLinkForList = [
    {
      label: "Provider Profile",
      link: "#",
      handleClick: () => { },
    },
  ];

  const isProfileCreated = Boolean(
    getCurrentUserDetails('provider')?.provider?.provider_id
  );

  console.log('testingggg', getCurrentUserDetails('provider'));

  const fetchProviderProfileDetails = async () => {
    try {
      setLoading(true);

      const response = await getProviderProfileDetails();

      if (response?.data?.success) {
        const profileData = response.data.data;
        setProviderProfileDetails(profileData);

        // sync with header
        updateSessionDetails("provider", {
          ...profileData,
          first_name:
            profileData.first_name || profileData.fullName?.split(" ")[0],
          last_name:
            profileData.last_name ||
            profileData.fullName?.split(" ").slice(1).join(" "),
          profile_photo:
            profileData.profile_photo ||
            profileData.profileImage ||
            profileData.profilePhoto,
        });
      }
    } catch (error) {
      console.error("PROVIDER PROFILE GET FAILED::", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviderProfileDetails();
  }, []);

  const handleProviderLogoutAfterPasswordChange = async () => {
    try {
      const response = await userLogout();
      if (response && response.status === 200) {
        logoutByUserType("provider");
      } else {
        logoutByUserType("provider");
      }
    } catch (err) {
      console.error("PROVIDER LOGOUT FAILED::", err);
      logoutByUserType("provider");
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box className="provider-profile-page__spinner-container">
          <CircularProgress size="3rem" />
        </Box>
      );
    }

    if (!isEditMode) {
      return (
        <>
          <ProviderProfileView
            isEditMode={isEditMode}
            onEdit={() => setIsEditMode(true)}
            providerProfileDetails={providerProfileDetails}
          />

          <SecuritySection onChangePassword={() => setOpenChangePwd(true)} />
          <LinkPartnerSection
            providerProfileDetails={providerProfileDetails}
            onSuccess={() => {
              setAlertMessage("Partner linked successfully");
              setAlertType("success");
              setAlertOpen(true);
              fetchProviderProfileDetails();
            }}
            onError={(msg) => {
              setAlertMessage(msg);
              setAlertType("error");
              setAlertOpen(true);
            }}
          />
        </>
      );
    }

    return (
      <>
        <ProviderProfileForm
          isProfileCreated={isProfileCreated}
          providerProfileDetails={providerProfileDetails}
          setProviderProfileDetails={setProviderProfileDetails}
          setIsEditMode={setIsEditMode}
          onCancel={() => setIsEditMode(false)}
          onSave={() => setIsEditMode(false)}
          fetchProviderProfileDetails={fetchProviderProfileDetails}
        />

        <SecuritySection onChangePassword={() => setOpenChangePwd(true)} />
        <LinkPartnerSection
          providerProfileDetails={providerProfileDetails}
          onSuccess={() => {
            setAlertMessage("Partner linked successfully");
            setAlertType("success");
            setAlertOpen(true);
            fetchProviderProfileDetails();
          }}
          onError={(msg) => {
            setAlertMessage(msg);
            setAlertType("error");
            setAlertOpen(true);
          }}
        />
      </>
    );
  };

  return (
    <div className="provider-profile-main-wrapper listing-page-main-wrapper">
      {/* ✅ Header: same pattern as ServiceList */}
      <ProfileHeader mode="provider" breadcrumbLink={breadcrumbLinkForList} />

      {/* ✅ Content wrapper: same padding pattern as ServiceList */}
      <div className="provider-profile-content-wrapper">
        {renderContent()}
      </div>

      <ChangePasswordModal
        open={openChangePwd}
        onClose={() => setOpenChangePwd(false)}
        onSuccess={() => {
          setOpenChangePwd(false);

          setAlertType("success");
          setAlertMessage("Password has been changed");
          setAlertOpen(true);

          setTimeout(() => {
            handleProviderLogoutAfterPasswordChange();
          }, 1200);
        }}
      />

      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
        autoHideDuration={3000}
      />
    </div>
  );
};

export default ProviderProfilePage;
